import React, { ReactNode, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Moon, Sun, BarChart3, TrendingUp, Bell, Settings, LogOut, Menu, X, TrendingDown, TrendingUpDown } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Button } from '../atoms/Button';
interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  current: boolean;
}

interface DashboardLayoutProps {
  children: ReactNode;
}

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();

  const navigation: NavigationItem[] = [
    { name: 'Dashboard', href: '/dashboard', icon: BarChart3, current: location.pathname === '/dashboard' },
    { name: 'Trading', href: '/trading', icon: TrendingUp, current: location.pathname === '/trading' },
    { name: 'Alerts', href: '/alerts', icon: Bell, current: location.pathname === '/alerts' },
    { name: 'Real-Time', href: '/realtime', icon: TrendingUp, current: location.pathname === '/realtime' },
    { name: 'Live Reports', href: '/live-reports', icon: TrendingUpDown, current: location.pathname === '/live-reports' },
    { name: 'Reports', href: '/reports', icon: Settings, current: location.pathname === '/reports' },
    
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-40 lg:hidden ${sidebarOpen ? '' : 'hidden'}`}>
        <div className="fixed inset-0 bg-black bg-opacity-25" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-white dark:bg-gray-800">
          <div className="flex h-16 items-center justify-between px-4 border-b border-gray-200 dark:border-gray-700">
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Atroverse Trading</h1>
            <Button variant="ghost" size="sm" onClick={() => setSidebarOpen(false)}>
              <X size={20} />
            </Button>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {navigation.map(item => (
              <button
                key={item.name}
                onClick={() => {
                  navigate(item.href);
                  setSidebarOpen(false);
                }}
                className={`group flex w-full items-center rounded-md px-2 py-2 text-sm font-medium ${item.current
                  ? 'bg-blue-100 text-blue-900 dark:bg-blue-900 dark:text-blue-200'
                  : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                  }`}
                data-testid={`nav-${item.name.toLowerCase()}`}
              >
                <item.icon className="mr-3 h-5 w-5" />
                {item.name}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-1 flex-col bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700">
          <div className="flex h-16 items-center px-4 border-b border-gray-200 dark:border-gray-700">
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Atroverse Trading</h1>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {navigation.map(item => (
              <button
                key={item.name}
                onClick={() => navigate(item.href)}
                className={`group flex w-full items-center rounded-md px-2 py-2 text-sm font-medium ${item.current
                  ? 'bg-blue-100 text-blue-900 dark:bg-blue-900 dark:text-blue-200'
                  : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                  }`}
                data-testid={`nav-${item.name.toLowerCase()}`}
              >
                <item.icon className="mr-3 h-5 w-5" />
                {item.name}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <div className="sticky top-0 z-10 flex h-16 bg-white dark:bg-gray-800 shadow border-b border-gray-200 dark:border-gray-700">
          <button
            type="button"
            className="border-r border-gray-200 px-4 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 lg:hidden"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-button"
          >
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex flex-1 justify-between px-4">
            <div className="flex items-center gap-4">
              <img
                src="https://atroverse.com/assets/atroverse-logo1.png"
                alt="Atroverse Logo"
                className="h-20 w-auto"
              />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Welcome back, {user?.name}
              </h2>
            </div>
            <div className="ml-4 flex items-center md:ml-6 space-x-2">
              <Button variant="ghost" size="sm" onClick={toggleTheme} data-testid="theme-toggle">
                {isDark ? <Sun size={20} /> : <Moon size={20} />}
              </Button>
              <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-button">
                <LogOut size={20} />
              </Button>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="py-6">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">{children}</div>
        </main>
      </div>
    </div>
  );
};